import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useIsMobile } from "~/components/hooks/use-mobile";
import { GrammarCardDisplay } from "~/components/review/grammar-card";
import { KanjiCardDisplay } from "~/components/review/kanji-card";
import { VocabularyCardDisplay } from "~/components/review/vocabulary-card";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import {
	useDueCardsCount,
	useFinishedCardsCount,
	useNextReviewCard,
	useSubmitReview,
	useSuspendCard,
} from "~/lib/client/fsrs";
import type {
	GrammarContent,
	KanjiContent,
	VocabularyContent,
} from "~/lib/server/db/schema/flashcard-schema";
import { m } from "~/i18n/messages";
import { useSetAtom } from "jotai";
import { currentPageContextAtom } from "~/components/practice/chat-widget";

function ReviewPageSkeleton() {
	return (
		<div className="container mx-auto max-w-screen-sm p-4">
			<div className="mb-6">
				<div className="flex items-center justify-between mb-2">
					<div className="text-lg font-medium">
						<Skeleton className="h-6 w-32 inline-block" />
					</div>
					<Skeleton className="h-6 w-24" />
				</div>
				<Skeleton className="h-2 w-full rounded-full" />
			</div>

			<Card className="mb-8 overflow-hidden border-2 border-primary/30 p-0">
				<CardContent className="flex min-h-[400px] flex-col items-center justify-center p-6">
					<Skeleton className="h-24 w-48 mb-4" />
					<Skeleton className="h-8 w-32 mb-8" />
					<Skeleton className="h-12 w-48" />
				</CardContent>
			</Card>

			<div className="fixed bottom-0 left-32 w-full z-20 bg-white border-t border-gray-200 p-4 flex justify-center">
				<div className="w-full max-w-screen-sm">
					<div className="flex w-full justify-center gap-3">
						<Skeleton className="h-12 w-48" />
					</div>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/app/learn/review/")({
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<SuspenseFallback
			loading={<ReviewPageSkeleton />}
			error={<div className="container mx-auto max-w-screen-sm p-4 text-center text-red-500">Error loading review page</div>}
		>
			<ReviewPageContent />
		</SuspenseFallback>
	);
}

function ReviewPageContent() {
	const setPageContext = useSetAtom(currentPageContextAtom);

	const [showAnswer, setShowAnswer] = useState(false);
	const { data: reviewData } = useNextReviewCard();
	const { data: finishedCardsCount } = useFinishedCardsCount();
	const { data: dueCardsCount } = useDueCardsCount();
	const submitReviewMutation = useSubmitReview();
	const suspendCardMutation = useSuspendCard();

	const currentCard = reviewData?.[0];

	const totalDueCards = (finishedCardsCount ?? 0) + (dueCardsCount ?? 0);

	const isMobile = useIsMobile();

	useEffect(() => {
		setPageContext({
			title: m["navbar.review"](),
			slug: "/app/learn/review",
			contextData: {
				totalDueCards,
				currentCard,
				finishedCardsCount,
			},
		});
	}, [setPageContext, totalDueCards, currentCard, finishedCardsCount]);

	useEffect(() => {
		if (isMobile) return;

		function handleKeyDown(e: KeyboardEvent) {
			if (e.repeat) return;
			const keyNum = Number.parseInt(e.key, 10);
			if (!Number.isNaN(keyNum)) {
				if (keyNum >= 1 && keyNum <= 4) {
					handleRate(keyNum as 1 | 2 | 3 | 4);
					return;
				}
				if (keyNum === 0) {
					handleSuspend();
					return;
				}
			}
			if (e.key === " ") {
				handleShowAnswer();
				return;
			}
		}
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isMobile]);

	function handleShowAnswer() {
		setShowAnswer(true);
	}

	function handleRate(rating: 1 | 2 | 3 | 4) {
		if (!currentCard?.card) return;

		submitReviewMutation.mutate(
			{
				cardId: currentCard.card.cid,
				rating: rating,
			},
			{
				onSuccess: (res) => {
					if (!res.success) {
						toast.error(
							m["review_process.submission_failed"]({ message: res.message }),
						);
					} else {
						toast.success(m["review_process.submitted_success"]());
					}
				},
				onError: (error) => {
					toast.error(m["errors.error_submitting_review"]());
				},
				onSettled: () => {
					setShowAnswer(false);
				},
			},
		);
	}

	function handleSuspend() {
		if (!currentCard?.card) return;

		suspendCardMutation.mutate(
			{ cardId: currentCard.card.cid },
			{
				onSuccess: (res) => {
					if (!res.success) {
						toast.error(
							m["review_process.submission_failed"]({ message: res.message }),
						);
					} else {
						toast.success(m["review_process.suspended_success"]());
					}
				},
				onError: (error) => {
					toast.error(m["errors.error_suspending_card"]());
				},
				onSettled: () => {
					setShowAnswer(false);
				},
			},
		);
	}

	const isPending =
		submitReviewMutation.isPending || suspendCardMutation.isPending;

	return (
		<>
			<div className="container mx-auto max-w-screen-sm p-4">
				<div className="mb-6">
					<div className="flex items-center justify-between mb-2">
						<div className="text-lg font-medium">
							<span className="text-pink-600">{m["quiz.reviewing"]()}</span>{" "}
							{m["quiz.cards_left"]({
								cards: totalDueCards - (finishedCardsCount ?? 0),
							})}
						</div>
						<div className="text-muted-foreground">
							{m["quiz.card_progress"]({
								current: (finishedCardsCount ?? 0) + 1,
								total: totalDueCards,
							})}
						</div>
					</div>
					<Progress
						value={finishedCardsCount ?? 0}
						max={Math.max(totalDueCards, 1)}
					/>
				</div>

				{reviewData?.length === 0 && (
					<Card className="mb-8 overflow-hidden border-2 border-primary/30 p-0">
						<CardContent className="flex min-h-[400px] flex-col items-center justify-center p-6 text-center">
							<h2 className="text-2xl font-semibold mb-4">
								{m["quiz.all_done"]()}
							</h2>
							<p className="text-muted-foreground">
								{m["quiz.all_done_message"]()}
							</p>
						</CardContent>
					</Card>
				)}

				{currentCard && (
					<Card className="mb-32 overflow-hidden border-2 border-primary/30 p-0">
						<CardContent className="flex min-h-[400px] flex-col items-center justify-center p-6">
							{currentCard.source.type === "vocabulary" && (
								<VocabularyCardDisplay
									data={currentCard.source.content as VocabularyContent}
									showAnswer={showAnswer}
									reading={currentCard.source.reading}
									id={currentCard.source.sid}
									level={currentCard.source.level}
								/>
							)}
							{currentCard.source.type === "grammar" && (
								<GrammarCardDisplay
									data={currentCard.source.content as GrammarContent}
									showAnswer={showAnswer}
									reading={currentCard.source.reading}
									id={currentCard.source.sid}
									level={currentCard.source.level}
								/>
							)}
							{currentCard.source.type === "kanji" && (
								<KanjiCardDisplay
									data={currentCard.source.content as KanjiContent}
									showAnswer={showAnswer}
									kanji={currentCard.source.reading ?? ""}
									id={currentCard.source.sid}
									level={currentCard.source.level}
								/>
							)}
						</CardContent>
					</Card>
				)}
			</div>

			{currentCard && (
				<div
					className="fixed bottom-0 left-32 w-full z-20 bg-white border-t border-gray-200 p-4 flex justify-center"
					style={{
						boxShadow: "0 -2px 16px 0 rgba(0,0,0,0.04)",
						backdropFilter: "blur(4px)",
					}}
				>
					<div className="w-full max-w-screen-sm">
						{!showAnswer ? (
							<div className="flex w-full justify-center gap-3">
								<Button
									size="lg"
									className="relative min-w-[200px] text-lg bg-pink-400 hover:bg-pink-500"
									onClick={handleShowAnswer}
								>
									Show
									{!isMobile && (
										<div className="absolute top-0 right-0 text-xs text-white bg-black/20 leading-none rounded-bl-[0.3rem] rounded-tr-md w-12 h-5 flex items-center justify-center">
											{m["ui.space"]()}
										</div>
									)}
								</Button>
							</div>
						) : (
							<div className="flex w-full justify-center gap-2 md:gap-3">
								<Button
									variant="destructive"
									size={isMobile ? "sm" : "lg"}
									className="relative flex-1 bg-red-400 hover:bg-red-500"
									onClick={() => handleRate(1)}
									disabled={isPending}
								>
									Again
									{!isMobile && (
										<div className="absolute top-0 right-0 text-xs text-white bg-black/20 leading-none rounded-bl-[0.3rem] rounded-tr-md size-5 flex items-center justify-center">
											1
										</div>
									)}
								</Button>
								<Button
									variant="default"
									size={isMobile ? "sm" : "lg"}
									className="relative flex-1 bg-rose-400 hover:bg-rose-500"
									onClick={() => handleRate(2)}
									disabled={isPending}
								>
									Hard
									{!isMobile && (
										<div className="absolute top-0 right-0 text-xs text-white bg-black/20 leading-none rounded-bl-[0.3rem] rounded-tr-md size-5 flex items-center justify-center">
											2
										</div>
									)}
								</Button>
								<Button
									variant="default"
									size={isMobile ? "sm" : "lg"}
									className="relative flex-1 bg-pink-400 hover:bg-pink-500"
									onClick={() => handleRate(3)}
									disabled={isPending}
								>
									Good
									{!isMobile && (
										<div className="absolute top-0 right-0 text-xs text-white bg-black/20 leading-none rounded-bl-[0.3rem] rounded-tr-md size-5 flex items-center justify-center">
											3
										</div>
									)}
								</Button>
								<Button
									variant="default"
									size={isMobile ? "sm" : "lg"}
									className="relative flex-1 bg-fuchsia-400 hover:bg-fuchsia-500"
									onClick={() => handleRate(4)}
									disabled={isPending}
								>
									Easy
									{!isMobile && (
										<div className="absolute top-0 right-0 text-xs text-white bg-black/20 leading-none rounded-bl-[0.3rem] rounded-tr-md size-5 flex items-center justify-center">
											4
										</div>
									)}
								</Button>
								{/* <Button
									variant="default"
									size="lg"
									className="relative md:flex-1 bg-slate-400 hover:bg-slate-500"
									onClick={handleSuspend}
									disabled={isPending}
								>
									Suspend
									{!isMobile && (
										<div className="absolute top-0 right-0 text-xs text-white bg-black/20 leading-none rounded-bl-[0.3rem] rounded-tr-md size-5 flex items-center justify-center">
											0
										</div>
									)}
								</Button> */}
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
}
